import React, { FC, PropsWithChildren, RefObject, createContext, useRef } from 'react';
import { Box, InputProps, useTheme } from '@chakra-ui/react';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import ReactDatePicker from 'react-datepicker';
import {
    formatCalendarYmdInTimeZone,
    parseCalendarYmdAsLocalDate
} from 'ts/common/components/date-picker/brandCalendarDateFormat';
import { noop } from 'ts/common/utils';
import DatePickerHeaderControls, {
    IDatePickerHeaderControlsProps
} from './DatePickerHeaderControls';
import DatePickerInput from './DatePickerInput';

interface IDatePickerProps {
    dateFormat?: string;
    id?: string;
    inline?: boolean;
    isDisabled?: boolean;
    isInvalid?: boolean;
    isValid?: boolean;
    max?: Date;
    min?: Date;
    name?: string;
    onBlur?: () => void;
    onChange: (value: Date | null) => void;
    placeholder?: string;
    value: Date | null;
    centeredDate?: boolean;
    availabilities?: {
        [date: string]: string[];
    };
    disableUnavailableDays?: boolean;
    disabledDates?: string[];
    inputProps?: Partial<InputProps>;
    showTodayButton?: boolean;
    brandTimezone?: string;
    /** Brand locale, e.g. `en_US` or `en-US`. Preferred over browser locale for display. */
    brandLocale?: string;
}

interface IDatePickerContext {
    close: () => void;
    dateFormat: string;
    inline?: boolean;
    isInvalid?: boolean;
    isValid?: boolean;
    max?: Date;
    min?: Date;
    open: () => void;
    onChange: (value: Date | null) => void;
    value: Date | null;
    centeredDate: boolean;
    showTodayButton: boolean;
    brandTimezone?: string;
    brandLocale?: string;
}

export interface ICustomDatePickerTheme {
    components: {
        DatePicker: {
            baseStyle: {
                fontFamily?: string;
                backgroundColor?: string;
                headerBackgroundColor?: string;
                color?: string;
                dateColor?: string;
                disabledColor?: string;
                // Additional styling properties
                calendarBackgroundColor?: string;
                popperBackgroundColor?: string;
                dayHeaderColor?: string;
                borderColor?: string;
                borderRadius?: string;
                boxShadow?: string;
                padding?: string;
                daySize?: string;
                dayNameWidth?: string;
                dayFontSize?: string;
                hoverBackgroundColor?: string;
                hoverBorderRadius?: string;
                weekGap?: string;
                weekPadding?: string;
                dayNamesGap?: string;
                // Header styling
                headerBorderColor?: string;
                headerPaddingBottom?: string;
                chevronSize?: string;
                chevronColor?: string;
                monthYearFontSize?: string;
                monthYearFontWeight?: string;
                // Today button styling
                todayButtonColor?: string;
            };
        };
    };
}

// Global styles component that receives theme values
const GlobalStyles: FC<{
    borderColor?: string;
    borderRadius?: string;
    boxShadow?: string;
    popperBackgroundColor?: string;
}> = ({
    borderColor = 'gray',
    borderRadius = '4px',
    boxShadow = 'none',
    popperBackgroundColor = 'white'
}) => (
    <Global
        styles={css`
            /* Floating UI sets inline z-index: 1 on the popper; !important beats that so we stay above Redactor
               (.rx-toolbar 10, .rx-dropdown 9999, fullscreen 10000). */
            .react-datepicker-popper {
                background-color: ${popperBackgroundColor};
                z-index: 11000 !important;
                border: 1px solid ${borderColor};
                border-radius: ${borderRadius};
                box-shadow: ${boxShadow};
            }

            .react-datepicker {
                border: none;
                font-family: inherit;
            }

            .react-datepicker__triangle {
                display: none;
            }
        `}
    />
);

const formatDateToString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const DatePickerCalendarContainer = styled(Box)(({ theme }) => {
    const customTheme = theme as ICustomDatePickerTheme;
    const { DatePicker } = customTheme.components;
    const {
        fontFamily,
        backgroundColor,
        headerBackgroundColor,
        color,
        dateColor,
        disabledColor,
        dayHeaderColor,
        calendarBackgroundColor,
        padding = '0',
        daySize = '42px',
        dayNameWidth = '40px',
        dayFontSize = 'inherit',
        hoverBackgroundColor = 'none',
        hoverBorderRadius = '50%',
        weekGap = '10px',
        weekPadding = '4px 10px',
        dayNamesGap = '10px',
        borderRadius = '4px'
    } = DatePicker.baseStyle;

    // Use dayHeaderColor if available, otherwise fall back to color
    const headerTextColor = dayHeaderColor || color;
    // Use calendarBackgroundColor if available, otherwise fall back to headerBackgroundColor or white
    const calendarBg = calendarBackgroundColor || headerBackgroundColor || 'white';

    return `
        font-family: ${fontFamily};
        background-color: ${calendarBg};
        padding: ${padding};
        border-radius: ${borderRadius};
        overflow: hidden;

        .react-datepicker__month-container {
            width: 100%;
        }

        .react-datepicker__header.react-datepicker__header--custom {
            background-color: ${headerBackgroundColor};
            border-bottom: 0px;
            padding-top: 0;
        }

        .react-datepicker__day {
            font-family: ${fontFamily};
            color: ${color};
            width: ${daySize};
            height: ${daySize};
            line-height: ${daySize};
            font-size: ${dayFontSize};
            font-weight: 400;
            margin: 2px;
            border-radius: 50%;
            transition: background-color 0.15s ease;

            &:hover {
                background-color: ${hoverBackgroundColor};
                border-radius: ${hoverBorderRadius};
            }

            @media (max-width: 768px) {
                width: 32px;
                height: 32px;
                line-height: 32px;
            }

            @media (max-width: 480px) {
                width: 32px;
                height: 32px;
                line-height: 32px;
            }
        }

        .react-datepicker__day-names {
            display: flex;
            justify-content: space-between;
            gap: ${dayNamesGap};
            padding: 8px 0px 8px;
            color: #000;
            font-size: 12px;
            font-style: normal;
            line-height: 12px;
            letter-spacing: 0.36px;
            text-transform: uppercase;

            .react-datepicker__day-name {
                color: ${headerTextColor};
                font-family: ${fontFamily};
                font-weight: 400;
                width: ${dayNameWidth};
                padding: 2px 10px 2px 10px;

                @media (max-width: 768px) {
                    width: 30px;
                    padding: 2px 5px;
                }

                @media (max-width: 480px) {
                    width: 28px;
                    padding: 2px;
                }
            }
        }

        .react-datepicker__week {
            display: flex;
            padding: ${weekPadding};
            justify-content: space-between;
            align-items: center;
            align-self: stretch;
            gap: ${weekGap};

            @media (max-width: 768px) {
                gap: 5px;
                padding-left: 5px;
                padding-right: 5px;
            }

            @media (max-width: 480px) {
                gap: 2px;
                padding-left: 2px;
                padding-right: 2px;
            }
        }

        .react-datepicker__month {
            margin: 0px;
        }

        .react-datepicker__day--keyboard-selected {
            background-color: transparent;
            color: ${color};
            font-weight: 400;

            &:hover {
                background-color: ${hoverBackgroundColor};
            }
        }

        /* ReactDatePicker selected prop is selectedForPicker: brand-zone YMD (formatCalendarYmdInTimeZone) as local midnight (parseCalendarYmdAsLocalDate) so the grid highlights the same calendar day as the input */
        .react-datepicker__day--selected {
            background-color: ${backgroundColor};
            border-radius: 50%;
            color: ${dateColor || 'white'};
            font-weight: 400;

            &:hover {
                background-color: ${backgroundColor};
            }
        }

        .react-datepicker__day--today {
            font-weight: 600;
        }

        .react-datepicker__day--disabled {
            color: ${disabledColor};
            cursor: default;
            background-color: transparent;

            &:hover {
                background-color: transparent;
            }
        }

        .react-datepicker__day--outside-month {
            color: ${disabledColor};
        }
    `;
});

const DatePickerCalendar: FC<PropsWithChildren<{ className?: string }>> = ({
    className,
    children
}) => {
    return (
        <DatePickerCalendarContainer className={className}>{children}</DatePickerCalendarContainer>
    );
};

const DatePickerHeader = (props: IDatePickerHeaderControlsProps) => (
    <DatePickerHeaderControls {...props} />
);

const DatePicker: FC<IDatePickerProps> = ({
    dateFormat = 'L',
    id,
    isDisabled,
    isInvalid,
    isValid,
    max,
    min,
    onChange = noop,
    placeholder,
    value,
    centeredDate = false,
    availabilities = [],
    disableUnavailableDays = false,
    disabledDates = [],
    inline,
    inputProps,
    showTodayButton = false,
    brandTimezone,
    brandLocale,
    ...props
}) => {
    const datePickerRef = useRef<ReactDatePicker>();
    const pickerSpreadProps = { ...(props as Record<string, unknown>) };
    delete pickerSpreadProps.selected;
    /** Normalize `selected` for the grid: brand-zone calendar YMD → local midnight via `brandCalendarDateFormat` (same wall date as the input). */
    const tz = brandTimezone?.trim() ?? '';

    const selectedForPicker: Date | null =
        value && tz
            ? parseCalendarYmdAsLocalDate(formatCalendarYmdInTimeZone(value, tz)) ?? value
            : value;

    const selectedLocalYmd = selectedForPicker ? formatDateToString(selectedForPicker) : '';

    const {
        components: {
            DatePicker: {
                baseStyle: {
                    color: themeColor,
                    borderColor,
                    borderRadius,
                    boxShadow,
                    popperBackgroundColor,
                    calendarBackgroundColor,
                    headerBackgroundColor
                }
            }
        }
    } = useTheme<ICustomDatePickerTheme>();

    const renderDayContents = (day: number, date: Date) => {
        const formattedDate = formatDateToString(date);
        const availability = availabilities[formattedDate];

        const hasTimeSlots = availability && availability.length > 0;
        const isSelected =
            !!selectedLocalYmd && !!date && formatDateToString(date) === selectedLocalYmd;
        const isWithinRange = (!min || date >= min) && (!max || date <= max);
        const isUnavailable = disableUnavailableDays && !hasTimeSlots && isWithinRange;
        const color = hasTimeSlots && !isSelected ? '#141617' : isUnavailable ? themeColor : '';

        return (
            <Box
                gap="10px"
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                borderRadius="50%"
                backgroundColor={hasTimeSlots && !isSelected ? '#EDF1F3' : 'transparent'}
                color={color}
            >
                {day}
            </Box>
        );
    };

    const filterDate = (date: Date) => {
        const formattedDate = formatDateToString(date); // Format date to 'YYYY-MM-DD'

        // Check if the formatted date string is in the disabledDates array
        const isDateDisabled = disabledDates.includes(formattedDate);

        // Check availability if disableUnavailableDays is enabled
        const availability = availabilities[formattedDate];
        const isAvailable = availability && availability.length > 0;

        if (disableUnavailableDays) {
            return isAvailable && !isDateDisabled;
        }

        // Disable only based on disabledDates if disableUnavailableDays is false
        return !isDateDisabled;
    };

    return (
        <>
            <GlobalStyles
                borderColor={borderColor}
                borderRadius={borderRadius}
                boxShadow={boxShadow}
                popperBackgroundColor={
                    popperBackgroundColor ||
                    calendarBackgroundColor ||
                    headerBackgroundColor ||
                    'white'
                }
            />
            <DatePickerContext.Provider
                value={{
                    close: () => {
                        if (datePickerRef && datePickerRef.current) {
                            datePickerRef.current.setOpen(false);
                        }
                    },
                    dateFormat,
                    inline,
                    isInvalid,
                    isValid,
                    max,
                    min,
                    open: () => {
                        if (datePickerRef && datePickerRef.current) {
                            datePickerRef.current.setOpen(true);
                        }
                    },
                    onChange,
                    value,
                    centeredDate,
                    showTodayButton,
                    brandTimezone,
                    brandLocale
                }}
            >
                <ReactDatePicker
                    {...pickerSpreadProps}
                    calendarContainer={DatePickerCalendar}
                    customInput={<DatePickerInput {...inputProps} />}
                    disabled={isDisabled}
                    filterDate={filterDate}
                    id={id}
                    inline={inline}
                    maxDate={max}
                    minDate={min}
                    onChange={onChange}
                    placeholderText={placeholder}
                    popperPlacement="bottom-start"
                    portalId="sp-react-datepicker-portal"
                    ref={datePickerRef as RefObject<ReactDatePicker>}
                    renderCustomHeader={DatePickerHeader}
                    renderDayContents={renderDayContents}
                    selected={selectedForPicker}
                    shouldCloseOnSelect={true}
                    useWeekdaysShort
                />
            </DatePickerContext.Provider>
        </>
    );
};

export const DatePickerContext = createContext<IDatePickerContext>({
    close: noop,
    dateFormat: 'L',
    inline: false,
    isInvalid: false,
    isValid: false,
    max: undefined,
    min: undefined,
    open: noop,
    onChange: noop,
    value: null,
    centeredDate: false,
    showTodayButton: false,
    brandTimezone: undefined,
    brandLocale: undefined
});

export default DatePicker;
